import {
  CommitInfo,
  explainSourceDiff,
  getSourceContent,
  handleLargeResponse
} from '_logics/LFCSourceUtil';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import LFCIconButton from '_components/inputs/LFCIconButton';
import {getLanguageByFileName} from '_logics/code-highlight';
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import DiffDisplay from './DiffDisplay';
import AiMarkdownViewer from './AiMarkdownViewer';
import {useMySettings} from '_contexts/MySettingsProvider';

type FileHistoryDialogProps = {
  open: boolean;
  onClose: () => void;
  filePath: string;
  fileHistory: CommitInfo[];
  historyLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadMoreLoading?: boolean;
};

/**
 * ファイルパスから devices/ プレフィックスを除去して表示用パスを取得
 */
const getDisplayPath = (filePath: string): string => {
  // "devices/device-001/path/to/file.txt" -> "device-001/path/to/file.txt"
  return filePath.replace(/^devices\//, '');
};

/**
 * 日付をフォーマットする
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return dateString;
  }
};

type CommitListProps = {
  commits: CommitInfo[];
  selectedIndex: number | null;
  disabledIndices: number[];
  onSelect: (index: number) => void;
  historyLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadMoreLoading?: boolean;
};

const CommitList = memo(
  ({
    commits,
    selectedIndex,
    disabledIndices,
    onSelect,
    historyLoading,
    hasMore,
    onLoadMore,
    loadMoreLoading
  }: CommitListProps) => (
    <List dense disablePadding sx={{flex: 1, overflow: 'auto'}}>
      {historyLoading ? (
        <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
          <CircularProgress size={24} />
        </Box>
      ) : commits.length === 0 ? (
        <ListItem>
          <ListItemText
            primary={
              <Typography variant="body2" color="text.secondary">
                履歴なし
              </Typography>
            }
          />
        </ListItem>
      ) : (
        <>
          {commits.map((commit, index) => {
            const isDisabled = disabledIndices.includes(index);
            const isSelected = selectedIndex === index;
            return (
              <ListItem key={commit.commitId} disablePadding divider>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => !isDisabled && onSelect(index)}
                  disabled={isDisabled}
                  sx={{
                    opacity: isDisabled ? 0.4 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.5
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isSelected ? 'bold' : 'normal',
                            color: isSelected ? 'primary.main' : 'text.primary'
                          }}
                        >
                          {formatDate(commit.date)}
                        </Typography>
                        {commit.changeType && (
                          <Chip
                            label={commit.changeType}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              bgcolor:
                                commit.changeType === 'A'
                                  ? 'success.main'
                                  : commit.changeType === 'D'
                                    ? 'error.main'
                                    : 'warning.main',
                              color: 'white'
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          {hasMore && (
            <ListItem sx={{justifyContent: 'center', py: 1}}>
              <Button
                size="small"
                onClick={onLoadMore}
                disabled={loadMoreLoading}
                startIcon={loadMoreLoading ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
              >
                {loadMoreLoading ? '...' : '更に'}
              </Button>
            </ListItem>
          )}
        </>
      )}
    </List>
  )
);

/**
 * ファイル変更履歴ダイアログ
 */
const FileHistoryDialog = ({
  open,
  onClose,
  filePath,
  fileHistory,
  historyLoading,
  hasMore,
  onLoadMore,
  loadMoreLoading
}: FileHistoryDialogProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mySettings = useMySettings();

  const [beforeIndex, setBeforeIndex] = useState<number | null>(null);
  const [afterIndex, setAfterIndex] = useState<number | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [beforeContent, setBeforeContent] = useState<string>('');
  const [afterContent, setAfterContent] = useState<string>('');
  const [changeType, setChangeType] = useState<'A' | 'M' | 'D' | undefined>(undefined);
  const [beforeExists, setBeforeExists] = useState<boolean>(true);
  const [afterExists, setAfterExists] = useState<boolean>(true);

  // 表示モードとズーム
  const [viewMode, setViewMode] = useState<'unified' | 'sideBySide'>('sideBySide');
  const [zoomPercent, setZoomPercent] = useState(100);
  const fontSize = (zoomPercent / 100) * 14;
  const handleZoomIn = () => setZoomPercent(prev => Math.min(prev + 5, 120));
  const handleZoomOut = () => setZoomPercent(prev => Math.max(prev - 5, 80));
  const handleResetZoom = () => setZoomPercent(100);

  // AI解説
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const aiAbortRef = useRef<AbortController | null>(null);
  const [aiWidth, setAiWidth] = useState(400);
  const aiResizing = useRef(false);

  const handleAiResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    aiResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!aiResizing.current) return;
      const dialogEl = document.getElementById('file-history-dialog-content');
      if (!dialogEl) return;
      const dialogRect = dialogEl.getBoundingClientRect();
      const newWidth = dialogRect.right - e.clientX;
      setAiWidth(Math.max(200, Math.min(newWidth, dialogRect.width * 0.6)));
    };
    const onMouseUp = () => {
      aiResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleAiExplain = useCallback(async () => {
    if (aiOpen && aiExplanation) {
      setAiOpen(false);
      return;
    }

    setAiOpen(true);
    setAiLoading(true);
    setAiExplanation(null);

    aiAbortRef.current?.abort();
    const abortController = new AbortController();
    aiAbortRef.current = abortController;

    const fileName = filePath.split('/').pop() || '';
    try {
      const result = await explainSourceDiff(
        filePath,
        fileName,
        beforeContent,
        afterContent,
        abortController.signal,
        mySettings.locale as 'ja' | 'en' | undefined
      );
      setAiExplanation(result.explanation);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'CanceledError') return;
      setAiExplanation('AI解説の取得に失敗しました。');
    } finally {
      setAiLoading(false);
    }
  }, [filePath, beforeContent, afterContent, aiOpen, aiExplanation, mySettings.locale]);

  // fileHistoryはAPIから新しい順（最新が先）で返されるので、そのまま使用
  const sortedHistory = fileHistory;

  // ダイアログが閉じたら選択状態とAI解説をリセット
  useEffect(() => {
    if (!open) {
      setAfterIndex(null);
      setBeforeIndex(null);
      setAiExplanation(null);
      setAiOpen(false);
      setAiLoading(false);
      aiAbortRef.current?.abort();
    }
  }, [open]);

  // ダイアログが開いたらデフォルト選択を設定（既に選択がある場合はキープ）
  useEffect(() => {
    if (open && !historyLoading && sortedHistory.length > 0) {
      // 既に選択がある場合はキープ（更に表示でリセットしない）
      if (afterIndex !== null) {
        return;
      }
      // デフォルト: After=最新(0), Before=1つ前(1) or null
      const newAfterIndex = 0;
      const newBeforeIndex = sortedHistory.length > 1 ? 1 : null;
      setAfterIndex(newAfterIndex);
      setBeforeIndex(newBeforeIndex);
    } else if (open && !historyLoading && sortedHistory.length === 0) {
      setAfterIndex(null);
      setBeforeIndex(null);
      setBeforeContent('');
      setAfterContent('');
      setChangeType(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, historyLoading, sortedHistory.length]);

  /**
   * ファイル内容を取得（404の場合は空文字列を返す）
   */
  const fetchContentSafe = async (
    path: string,
    commitId: string
  ): Promise<{content: string; notFound: boolean; isLarge: boolean}> => {
    try {
      const result = await getSourceContent(path, commitId);
      if (result.type === 'large-response') {
        return {content: '', notFound: false, isLarge: true};
      }
      return {content: result.content || '', notFound: false, isLarge: false};
    } catch (error: any) {
      // 404エラー（ファイルが存在しない = 削除されたか、まだ作成されていない）
      if (error?.response?.status === 404) {
        return {content: '', notFound: true, isLarge: false};
      }
      throw error;
    }
  };

  // 選択中のコミットID（依存配列用）
  const afterCommitId = afterIndex !== null ? sortedHistory[afterIndex]?.commitId : null;
  const beforeCommitId = beforeIndex !== null ? sortedHistory[beforeIndex]?.commitId : null;

  // コミット選択が変わったらAI解説をリセット
  useEffect(() => {
    setAiExplanation(null);
    setAiOpen(false);
    aiAbortRef.current?.abort();
  }, [beforeCommitId, afterCommitId]);

  // コミットID変更時に差分を取得
  useEffect(() => {
    if (!open || !afterCommitId) {
      return;
    }

    const fetchDiff = async () => {
      setDiffLoading(true);

      try {
        const [afterResult, beforeResult] = await Promise.all([
          fetchContentSafe(filePath, afterCommitId),
          beforeCommitId
            ? fetchContentSafe(filePath, beforeCommitId)
            : Promise.resolve({content: '', notFound: true, isLarge: false})
        ]);

        // ファイル存在フラグを更新
        setBeforeExists(!beforeResult.notFound && !beforeResult.isLarge);
        setAfterExists(!afterResult.notFound && !afterResult.isLarge);

        if (afterResult.isLarge || beforeResult.isLarge) {
          setAfterContent('(大容量ファイルのため差分表示できません)');
          setBeforeContent('');
          setChangeType(undefined);
        } else {
          if (beforeResult.notFound && !afterResult.notFound) {
            setChangeType('A');
          } else if (!beforeResult.notFound && afterResult.notFound) {
            setChangeType('D');
          } else {
            setChangeType('M');
          }
          setAfterContent(afterResult.content);
          setBeforeContent(beforeResult.content);
        }
      } catch (error) {
        console.error('差分取得エラー:', error);
        setAfterContent('(差分の取得に失敗しました)');
        setBeforeContent('');
      } finally {
        setDiffLoading(false);
      }
    };

    fetchDiff();
  }, [open, beforeCommitId, afterCommitId, filePath]);

  /**
   * Before選択ハンドラ
   */
  const handleSelectBefore = useCallback(
    (index: number) => {
      // Afterより古い（indexが大きい）コミットのみ選択可能
      if (afterIndex !== null && index <= afterIndex) {
        return;
      }
      setBeforeIndex(index);
    },
    [afterIndex]
  );

  /**
   * After選択ハンドラ
   */
  const handleSelectAfter = useCallback(
    (index: number) => {
      // Beforeより新しい（indexが小さい）コミットのみ選択可能
      if (beforeIndex !== null && index >= beforeIndex) {
        return;
      }
      setAfterIndex(index);
    },
    [beforeIndex]
  );

  // Before選択時の無効インデックス（Afterより新しいコミットは選択不可）
  const beforeDisabledIndices = useMemo(
    () => (afterIndex !== null ? sortedHistory.map((_, i) => i).filter(i => i <= afterIndex) : []),
    [afterIndex, sortedHistory]
  );

  // After選択時の無効インデックス（Beforeより古いコミットは選択不可）
  const afterDisabledIndices = useMemo(
    () =>
      beforeIndex !== null ? sortedHistory.map((_, i) => i).filter(i => i >= beforeIndex) : [],
    [beforeIndex, sortedHistory]
  );

  // サイドバーコンポーネントをメモ化
  const beforeSidebar = useMemo(
    () => (
      <CommitList
        commits={sortedHistory}
        selectedIndex={beforeIndex}
        disabledIndices={beforeDisabledIndices}
        onSelect={handleSelectBefore}
        historyLoading={historyLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        loadMoreLoading={loadMoreLoading}
      />
    ),
    [
      sortedHistory,
      beforeIndex,
      beforeDisabledIndices,
      handleSelectBefore,
      historyLoading,
      hasMore,
      onLoadMore,
      loadMoreLoading
    ]
  );

  const afterSidebar = useMemo(
    () => (
      <CommitList
        commits={sortedHistory}
        selectedIndex={afterIndex}
        disabledIndices={afterDisabledIndices}
        onSelect={handleSelectAfter}
        historyLoading={historyLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        loadMoreLoading={loadMoreLoading}
      />
    ),
    [
      sortedHistory,
      afterIndex,
      afterDisabledIndices,
      handleSelectAfter,
      historyLoading,
      hasMore,
      onLoadMore,
      loadMoreLoading
    ]
  );

  /**
   * 日時文字列をファイル名用にフォーマットする（YYYYMMDD_HHmmss）
   */
  const formatDateForFileName = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const mi = String(date.getMinutes()).padStart(2, '0');
      const ss = String(date.getSeconds()).padStart(2, '0');
      return `${yyyy}${mm}${dd}_${hh}${mi}${ss}`;
    } catch {
      return 'unknown';
    }
  };

  /**
   * ファイルをダウンロードする
   */
  const handleDownload = async (commitId: string, commitDate: string) => {
    try {
      const result = await getSourceContent(filePath, commitId);

      let content: string;
      if (result.type === 'large-response' && result.url) {
        // large-responseの場合はURLからコンテンツを取得
        const largeContent = await handleLargeResponse<{content: string}>(result);
        content = largeContent.content;
      } else {
        content = result.content || '';
      }

      // ファイル名を生成（ベース名_日時.拡張子）
      const originalFileName = filePath.split('/').pop() || 'file';
      const lastDotIndex = originalFileName.lastIndexOf('.');
      const baseName =
        lastDotIndex > 0 ? originalFileName.substring(0, lastDotIndex) : originalFileName;
      const ext = lastDotIndex > 0 ? originalFileName.substring(lastDotIndex) : '';
      const dateStr = formatDateForFileName(commitDate);
      const downloadFileName = `${baseName}_${dateStr}${ext}`;

      // Blobを作成してダウンロード
      const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('ダウンロードエラー:', error);
    }
  };

  /**
   * Beforeファイルのダウンロード
   */
  const beforeCommit = beforeIndex !== null ? sortedHistory[beforeIndex] : null;
  const handleDownloadBefore =
    beforeExists && beforeCommit
      ? () => handleDownload(beforeCommit.commitId, beforeCommit.date)
      : null;

  /**
   * Afterファイルのダウンロード
   */
  const afterCommit = afterIndex !== null ? sortedHistory[afterIndex] : null;
  const handleDownloadAfter =
    afterExists && afterCommit
      ? () => handleDownload(afterCommit.commitId, afterCommit.date)
      : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle
        sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5}}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1}}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={getDisplayPath(filePath)}
          >
            {getDisplayPath(filePath)}
          </Typography>
          {(() => {
            const language = getLanguageByFileName(filePath);
            return language ? (
              <Typography
                variant="caption"
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  flexShrink: 0
                }}
              >
                {language.badge}
              </Typography>
            ) : null;
          })()}
        </Box>
        {/* ツールバー */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Tooltip title="縮小">
            <span>
              <LFCIconButton
                color="primary"
                size="small"
                onClick={handleZoomOut}
                disabled={zoomPercent <= 80}
              >
                <RemoveIcon fontSize="small" />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Tooltip title="リセット">
            <Typography
              variant="body2"
              sx={{minWidth: 40, textAlign: 'center', fontWeight: 'bold', cursor: 'pointer'}}
              onClick={handleResetZoom}
            >
              {zoomPercent}%
            </Typography>
          </Tooltip>
          <Tooltip title="拡大">
            <span>
              <LFCIconButton
                color="primary"
                size="small"
                onClick={handleZoomIn}
                disabled={zoomPercent >= 120}
              >
                <AddIcon fontSize="small" />
              </LFCIconButton>
            </span>
          </Tooltip>
          <Box sx={{width: 8}} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
            color="primary"
          >
            <ToggleButton value="unified">
              <Tooltip title="行単位表示">
                <ViewStreamIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="sideBySide">
              <Tooltip title="横並び表示">
                <ViewColumnIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{width: 24}} />
          <LFCIconButton onClick={() => onClose()} size="small">
            <CloseIcon />
          </LFCIconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent
        id="file-history-dialog-content"
        sx={{p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'row'}}
      >
        {/* DiffDisplay */}
        <Box sx={{flex: 1, overflow: 'hidden'}}>
          <DiffDisplay
            beforeContent={beforeContent}
            afterContent={afterContent}
            changeType={changeType}
            maxHeight="100%"
            fileName={getDisplayPath(filePath)}
            onDownloadBefore={handleDownloadBefore}
            onDownloadAfter={handleDownloadAfter}
            hideToolbar={true}
            viewMode={viewMode}
            fontSize={fontSize}
            beforeSidebar={beforeSidebar}
            afterSidebar={afterSidebar}
            loading={diffLoading || afterIndex === null}
            onAiExplain={handleAiExplain}
            aiOpen={aiOpen}
            aiLoading={aiLoading}
          />
        </Box>

        {/* AI解説パネル（右側固定） */}
        {aiOpen && (
          <>
            <Box
              onMouseDown={handleAiResizeStart}
              sx={{
                width: 6,
                cursor: 'col-resize',
                bgcolor: isDark ? 'grey.700' : 'grey.300',
                '&:hover': {bgcolor: 'primary.main', opacity: 0.5},
                transition: 'background-color 0.15s',
                flexShrink: 0
              }}
            />
            <Box
              sx={{
                width: aiWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderLeft: 1,
                borderColor: 'divider',
                bgcolor: isDark ? alpha('#1a237e', 0.15) : alpha('#e3f2fd', 0.5)
              }}
            >
              <AiMarkdownViewer content={aiExplanation || ''} loading={aiLoading} />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FileHistoryDialog;
